import { useEffect, useMemo, useState } from 'react'
import { fxOrdersApi, fxCustomersApi, fxProductsApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

type OrderStatus = 'pendiente' | 'confirmado' | 'cancelado'
type OrderSource = 'manual' | 'formulario'

interface Order {
  id: number
  customer_id: number
  customer_name: string
  customer_phone: string | null
  product_id: number
  product_name: string
  quantity: number
  status: OrderStatus
  source: OrderSource
  sale_id: number | null
  notes: string | null
  created_at: string
}

interface Customer {
  id: number
  name: string
  phone: string | null
  is_active: boolean
}

interface Product {
  id: number
  name: string
  stock_qty: number
  is_active: boolean
}

interface NewOrderForm {
  customer_id: string
  product_id: string
  quantity: string
  notes: string
}

const emptyNewOrderForm: NewOrderForm = { customer_id: '', product_id: '', quantity: '1', notes: '' }

const STATUS_LABELS: Record<OrderStatus, string> = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  cancelado: 'Cancelado',
}
const STATUS_BADGE_CLASSES: Record<OrderStatus, string> = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  confirmado: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
}

/** Review queue for pre-sale requests -- from the Google Form shared over
 * WhatsApp/Facebook (source='formulario') or typed in directly from a
 * conversation (source='manual'). Confirming here is the ONLY way one of
 * these becomes a real distribution_fx.sales row (stock decrement, cash
 * position impact) -- see infra/db/030_add_fx_orders.sql for why a form
 * submission must never do that automatically. */
export default function FxOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<'' | OrderStatus>('pendiente')

  const [showForm, setShowForm] = useState(false)
  const [newOrderForm, setNewOrderForm] = useState<NewOrderForm>(emptyNewOrderForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [editingOrder, setEditingOrder] = useState<Order | null>(null)
  const [editQuantity, setEditQuantity] = useState('')
  const [editError, setEditError] = useState('')
  const [actionError, setActionError] = useState('')
  const [actingId, setActingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      const [ordersRes, customersRes, productsRes] = await Promise.all([
        fxOrdersApi.list(),
        fxCustomersApi.list(),
        fxProductsApi.list(),
      ])
      setOrders(ordersRes.data.orders || [])
      setCustomers((customersRes.data.customers || []).filter((c: Customer) => c.is_active))
      setProducts((productsRes.data.products || []).filter((p: Product) => p.is_active))
    } catch (err) {
      console.error('Error fetching FX orders:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const summary = useMemo(() => {
    const pendiente = orders.filter((o) => o.status === 'pendiente').length
    const confirmado = orders.filter((o) => o.status === 'confirmado').length
    const cancelado = orders.filter((o) => o.status === 'cancelado').length
    return { total: orders.length, pendiente, confirmado, cancelado }
  }, [orders])

  const filtered = filterStatus ? orders.filter((o) => o.status === filterStatus) : orders
  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    key: 'created_at',
    direction: 'desc',
  })

  const openNew = () => {
    setNewOrderForm(emptyNewOrderForm)
    setError('')
    setShowForm(true)
  }

  const handleNewOrderChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setNewOrderForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newOrderForm.customer_id || !newOrderForm.product_id) {
      setError('Selecciona un cliente y un producto')
      return
    }
    const quantity = parseInt(newOrderForm.quantity, 10)
    if (isNaN(quantity) || quantity <= 0) {
      setError('Ingresa una cantidad válida')
      return
    }
    setSaving(true)
    setError('')
    try {
      await fxOrdersApi.create({
        customer_id: Number(newOrderForm.customer_id),
        product_id: Number(newOrderForm.product_id),
        quantity,
        notes: newOrderForm.notes || null,
      })
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrar la solicitud')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (o: Order) => {
    setEditingOrder(o)
    setEditQuantity(String(o.quantity))
    setEditError('')
  }

  const handleSaveEdit = async () => {
    if (!editingOrder) return
    const quantity = parseInt(editQuantity, 10)
    if (isNaN(quantity) || quantity <= 0) {
      setEditError('Ingresa una cantidad válida')
      return
    }
    try {
      await fxOrdersApi.update(editingOrder.id, { quantity })
      setEditingOrder(null)
      fetchAll()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar')
    }
  }

  const handleConfirm = async (order: Order) => {
    if (!confirm(`¿Confirmar esta solicitud como venta real de ${order.quantity} x ${order.product_name}? Esto descuenta stock y queda registrado en caja.`)) return
    setActingId(order.id)
    setActionError('')
    try {
      await fxOrdersApi.confirm(order.id)
      fetchAll()
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Error al confirmar la venta')
    } finally {
      setActingId(null)
    }
  }

  const handleCancel = async (order: Order) => {
    if (!confirm('¿Cancelar esta solicitud?')) return
    setActingId(order.id)
    setActionError('')
    try {
      await fxOrdersApi.cancel(order.id)
      fetchAll()
    } catch (err) {
      console.error('Error cancelling FX order:', err)
    } finally {
      setActingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-espresso-800">Solicitudes — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nueva Solicitud
        </button>
      </div>
      <p className="text-sm text-espresso-500 mb-6">
        Pedidos del formulario (WhatsApp/Facebook) o cargados a mano. Ninguno afecta stock ni caja hasta que lo confirmes como venta.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Total</div>
          <div className="mt-1 text-2xl font-bold text-espresso-800">{summary.total}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Pendientes</div>
          <div className="mt-1 text-2xl font-bold text-yellow-700">{summary.pendiente}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Confirmados</div>
          <div className="mt-1 text-2xl font-bold text-green-700">{summary.confirmado}</div>
        </div>
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Cancelados</div>
          <div className="mt-1 text-2xl font-bold text-red-600">{summary.cancelado}</div>
        </div>
      </div>

      {actionError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4">{actionError}</div>}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nueva Solicitud</h2>
            </div>
            <form onSubmit={handleCreateOrder} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Cliente *</label>
                <select
                  name="customer_id" value={newOrderForm.customer_id} onChange={handleNewOrderChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="">Selecciona un cliente</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Producto *</label>
                  <select
                    name="product_id" value={newOrderForm.product_id} onChange={handleNewOrderChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Selecciona un producto</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} (stock: {p.stock_qty})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Cantidad *</label>
                  <input
                    type="number" min="1" name="quantity" value={newOrderForm.quantity} onChange={handleNewOrderChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea
                  name="notes" value={newOrderForm.notes} onChange={handleNewOrderChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Editar cantidad — {editingOrder.product_name}</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {editError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{editError}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Cantidad</label>
                <input
                  type="number" min="1" value={editQuantity} onChange={(e) => setEditQuantity(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setEditingOrder(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="button" onClick={handleSaveEdit}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700">
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-cream-50 shadow rounded-lg p-4 mb-4 flex items-center gap-3">
        <label className="text-xs font-medium text-espresso-500">Estado</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as '' | OrderStatus)}
          className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
        >
          <option value="">Todos</option>
          <option value="pendiente">Pendientes</option>
          <option value="confirmado">Confirmados</option>
          <option value="cancelado">Cancelados</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay solicitudes en este estado.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Fecha" sortKey="created_at" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Cliente" sortKey="customer_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Producto" sortKey="product_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Cantidad" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Origen" sortKey="source" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedData.map((o) => (
                  <tr key={o.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-espresso-800">{o.customer_name}</div>
                      {o.customer_phone && <div className="text-xs text-espresso-500">{o.customer_phone}</div>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{o.product_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{o.quantity}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {o.source === 'formulario' ? 'Formulario' : 'Manual'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_BADGE_CLASSES[o.status]}`}>
                        {STATUS_LABELS[o.status]}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      {o.status === 'pendiente' ? (
                        <div className="flex items-center justify-end gap-1">
                          <TableActionButton onClick={() => openEdit(o)} variant="primary" disabled={actingId === o.id}>
                            Editar
                          </TableActionButton>
                          <TableActionButton onClick={() => handleConfirm(o)} variant="success" disabled={actingId === o.id}>
                            Confirmar venta
                          </TableActionButton>
                          <TableActionButton onClick={() => handleCancel(o)} variant="danger" disabled={actingId === o.id}>
                            Cancelar
                          </TableActionButton>
                        </div>
                      ) : (
                        <span className="text-espresso-400 text-xs">
                          {o.status === 'confirmado' ? `Venta #${o.sale_id}` : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
